import assert from './assert';
import { getOperationName } from './operation';

export default class Transaction {
  static STATE_IDLE = Symbol('STATE_IDLE');

  static STATE_PENDING = Symbol('STATE_PENDING');

  static STATE_COMPLETE = Symbol('STATE_COMPLETE');

  constructor(type, entity, context) {
    this.type = type;
    this.state = Transaction.STATE_IDLE;
    this.entity = entity;
    this.history = {
      [this.getTimestamp(entity)]: entity,
    };
    this.context = context;
    this.error = null;
    this.tries = 0;
    this.issued = Date.now();
  }

  get result() {
    return this.entity;
  }

  get canMerge() {
    return this.state === Transaction.STATE_IDLE;
  }

  get isPending() {
    return this.state === Transaction.STATE_PENDING;
  }

  get isComplete() {
    return this.state === Transaction.STATE_COMPLETE;
  }

  get didSucceed() {
    return this.isComplete && this.error === null;
  }

  cloneWithContext(type, entity) {
    return new Transaction(type, entity, this.context);
  }

  getTimestamp(entity) {
    if ('modified' in entity) {
      return new Date(entity.modified).getTime();
    }
    return Date.now();
  }

  build(toPointInTime = NaN) {
    const { history } = this;
    const result = {};
    Object.keys(history)
      .map(key => parseInt(key, 10))
      .sort((a, b) => a - b)
      .forEach(timestamp => {
        if (!isNaN(toPointInTime) && timestamp > toPointInTime) {
          return;
        }
        Object.assign(result, history[timestamp]);
      });
    return result;
  }

  prepend(model) {
    let time = Object.keys(this.history)
      .map(key => parseInt(key, 10))
      .sort((a, b) => a - b)[0];

    if (time === undefined) {
      time = Date.now();
    }

    this.history[time - 1] = model;
    this.entity = this.build();
    this.commit();
  }

  append(model, atTime = NaN) {
    this.history[atTime || this.getTimestamp(model)] = model;
    this.entity = this.build();
    this.commit();
  }

  appendToLocalState(model) {
    Object.assign(this.entity, model);
    return this;
  }

  merge(transaction) {
    assert(
      this.type === transaction.type,
      `
      Unable to merge operations of different type.
      Trying to merge ${getOperationName(this.type)} with ${getOperationName(transaction.type)}.
    `
    );

    // merge the history of the other transaction, this way we are not
    // discarding any data and can rebuild into any point
    Object.keys(transaction.history).forEach(ts => {
      if (this.history[ts]) {
        Object.assign(this.history[ts], transaction.history[ts]);
      } else {
        this.history[ts] = transaction.history[ts];
      }
    });

    // now build up the entity to the latest known state
    this.entity = this.build();

    return this;
  }

  commit() {
    this.context.accept(this);
  }

  run() {
    if (this.state !== Transaction.STATE_IDLE) {
      throw new Error('Cannot run transaction in non-idle state');
    }

    this.issued = Date.now();
    this.state = Transaction.STATE_PENDING;

    return new Promise((resolve, reject) => {
      const future = this.context.run(this.entity);
      if (future instanceof Promise) {
        future.then(resolve).catch(reject);
      } else {
        reject(new Error('Context run method did not return a promise'));
      }
    });
  }

  complete() {
    this.state = Transaction.STATE_COMPLETE;
    this.error = null;
  }

  fail(error = null) {
    this.state = Transaction.STATE_COMPLETE;
    this.error = error;

    if (typeof this.context.fail === 'function') {
      this.context.fail(this, error);
    }
  }
}
